import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { Role, RoleArgOption, RoleResMock } from '../types/role.type'

export const useRoles = (arg?: RoleArgOption) => {
  const page = arg?.page
  const pageLimit = arg?.pageLimit
  const query = arg?.query
  const parent = arg?.parent
  const state = arg?.default
  const id = arg?.id
  let endpoint = '/roles?'
  if (page || pageLimit) endpoint = `${endpoint}page%5Bnumber%5D=${page || 1}&page%5Bsize%5D=${pageLimit || 20}`
  if (query) endpoint = `${endpoint}&query=${query}`
  if (state) endpoint = `${endpoint}&default=${state}`
  if (parent) endpoint = `${endpoint}&parent=${parent}`

  const { data: roles } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RoleResMock<Role[]>>(endpoint)
      return data
    },
    queryKey: ['roles', { page, pageLimit, parent, query, state }]
  })

  const { data: role, isLoading: roleIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RoleResMock<Role>>(`roles/${id}`)
      return data.data.attributes
    },
    queryKey: ['roles', { id }]
  })

  const { mutateAsync: updateRole } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`roles/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['roles', { id }] })
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
    }
  })

  const { mutateAsync: addRole, isPending: addRoleIsPending } = useMutation({
    mutationFn: async ({ formVal }: any) => {
      const { data } = await AXIOS_INSTANCE.post('roles', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    }
  })

  const { mutateAsync: deleteRole } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await AXIOS_INSTANCE.delete(`roles/${id}`, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    }
  })

  return {
    addRole,
    addRoleIsPending,
    deleteRole,
    role,
    roleIsLoading,
    roles: roles?.data,
    updateRole
  }
}
